import { alpha, styled, TextField } from '@mui/material';

export const EntityGrid = styled('div', {
  shouldForwardProp: (prop) => prop !== 'minTile',
})<{ minTile?: number }>(({ theme, minTile = 220 }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: `repeat(auto-fill, minmax(${minTile}px, 1fr))`,
}));

const cardBase = (theme: import('@mui/material').Theme) => ({
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  borderRadius: '14px',
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['transform', 'border-color', 'box-shadow', 'background-color']),
});

export const EntityCardRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'minHeight' && prop !== 'clickable',
})<{ minHeight?: number; clickable?: boolean }>(({ theme, minHeight = 184, clickable = true }) => ({
  ...cardBase(theme),
  minHeight,
  padding: theme.spacing(2.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  cursor: clickable ? 'pointer' : 'default',
  ...(clickable && {
    '&:hover': {
      borderColor: theme.palette.accent.main,
      transform: 'translateY(-2px)',
      backgroundColor: alpha(theme.palette.accent.main, 0.06),
      boxShadow: `0 12px 30px -14px ${alpha(theme.palette.accent.main, 0.55)}`,
    },
    '&:hover .entity-card-actions': { opacity: 1 },
  }),
}));

export const AddCardRoot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'minHeight',
})<{ minHeight?: number }>(({ theme, minHeight = 184 }) => ({
  ...cardBase(theme),
  minHeight,
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(2.5),
  borderStyle: 'dashed',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  font: 'inherit',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
  '& svg': { color: theme.palette.text.secondary, transition: theme.transitions.create('color') },
  '&:hover': {
    borderColor: theme.palette.accent.main,
    color: theme.palette.accent.main,
    transform: 'translateY(-2px)',
    backgroundColor: alpha(theme.palette.accent.main, 0.07),
    boxShadow: `0 12px 30px -14px ${alpha(theme.palette.accent.main, 0.5)}`,
    '& svg': { color: theme.palette.accent.main },
  },
}));

export const SectionToolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
}));

export const CountBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.25),
  borderRadius: '999px',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: theme.palette.info.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  whiteSpace: 'nowrap',
}));

export const SettingsSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: theme.palette.background.default,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.5) },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  },
  '& .MuiInputAdornment-root': { color: theme.palette.text.secondary },
}));

export const PrimaryActionButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.75),
  borderRadius: '10px',
  border: `1px solid ${theme.palette.accent.main}`,
  backgroundColor: theme.palette.accent.main,
  color: theme.palette.accent.contrastText,
  font: 'inherit',
  fontSize: '0.82rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
  '& svg': { color: theme.palette.accent.contrastText },
  '&:hover': {
    backgroundColor: theme.palette.accent.dark,
    borderColor: theme.palette.accent.dark,
    boxShadow: `0 8px 22px -14px ${alpha(theme.palette.accent.main, 0.9)}`,
  },
}));

export const ScopeGroup = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  padding: 3,
  gap: 2,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

export const ScopeButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  border: 'none',
  borderRadius: '7px',
  padding: theme.spacing(0.75, 1.75),
  font: 'inherit',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.18) : 'transparent',
  transition: theme.transitions.create(['background-color', 'color']),
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.primary.main, selected ? 0.24 : 0.08),
  },
}));
